import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";

export default function ProjectileMotionCalculator() {
  const [velocity, setVelocity] = useState("");
  const [angle, setAngle] = useState("");
  const [results, setResults] = useState<{
    range: number;
    maxHeight: number;
    timeOfFlight: number;
  } | null>(null);

  const calculateProjectile = () => {
    const v = parseFloat(velocity);
    const a = parseFloat(angle);

    if (isNaN(v) || isNaN(a) || v <= 0 || a < 0 || a > 90) {
      toast.error("Please enter valid values (angle: 0-90°)");
      return;
    }

    const g = 9.81; // gravity m/s²
    const radians = (a * Math.PI) / 180;
    
    const range = (v * v * Math.sin(2 * radians)) / g;
    const maxHeight = (v * v * Math.sin(radians) * Math.sin(radians)) / (2 * g);
    const timeOfFlight = (2 * v * Math.sin(radians)) / g;

    setResults({ range, maxHeight, timeOfFlight });
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setVelocity("");
    setAngle("");
    setResults(null);
  };

  return (
    <PageLayout 
      calculatorName="Projectile Motion Calculator" 
      calculatorPath="/calculator/projectile-motion"
      category="physics"
      subcategory="Mechanics"
    >
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Projectile Motion Calculator</h1>
            <p className="text-muted-foreground">
              Calculate range, maximum height, and time of flight for projectile motion
            </p>
          </div>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Projectile Motion Formulas</CardTitle>
              <CardDescription>
                Based on initial velocity and launch angle (assuming no air resistance)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="velocity">Initial Velocity (m/s)</Label>
                  <Input
                    id="velocity"
                    type="number"
                    step="0.1"
                    placeholder="Enter initial velocity"
                    value={velocity}
                    onChange={(e) => setVelocity(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="angle">Launch Angle (degrees)</Label>
                  <Input
                    id="angle"
                    type="number"
                    step="1"
                    min="0"
                    max="90"
                    placeholder="Enter angle (0-90°)"
                    value={angle}
                    onChange={(e) => setAngle(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={calculateProjectile} className="flex-1">
                  <Calculator className="mr-2 h-4 w-4" />
                  Calculate
                </Button>
                <Button onClick={reset} variant="outline">
                  Reset
                </Button>
              </div>

              {results && (
                <>
                  <div className="space-y-3">
                    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                      <CardHeader>
                        <CardTitle className="text-lg">Range</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold text-primary">
                          {results.range.toFixed(2)} m
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20">
                      <CardHeader>
                        <CardTitle className="text-lg">Maximum Height</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold text-accent">
                          {results.maxHeight.toFixed(2)} m
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                      <CardHeader>
                        <CardTitle className="text-lg">Time of Flight</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold text-primary">
                          {results.timeOfFlight.toFixed(2)} s
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <CalculatorSuperCalcCTA config={calculatorCTAs["projectile-motion"]} trigger={results.range} />
                </>
              )}

              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">How to use</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>1. Enter the initial velocity in meters per second</p>
                  <p>2. Enter the launch angle in degrees (0-90°)</p>
                  <p>3. Click Calculate to see all results</p>
                  <p className="pt-2 font-medium text-foreground">
                    Example: 20 m/s at 45° gives maximum range
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
    </PageLayout>
  );
}
